import { X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { TaxOverrideBadge, describeTaxOverride } from "@/components/common/TaxOverrideBadge";
import { RailCard, RailRow, railInput, railInputBase, railLink, Chip } from "./RailCard";
import { taxHint } from "./railMath";

const pct = (v) => `${parseFloat(v) || 0}%`;

function TaxRateRow({ form, setForm, proposalId, canOverrideTax, onOverride }) {
  const ov = form.tax_override;
  const customerRule = String(form.tax_breakdown?.state || "").startsWith("CUSTOMER OVERRIDE");
  return (
    <RailRow label="Tax Rate" testId="rail-tax-rate-row">
      {ov ? <Chip kind="manual" title="Manually overridden" testId="proposal-tax-chip">Override {describeTaxOverride(ov)}</Chip>
        : form.tax_rate_manual ? <Chip kind="manual" title="Manual rate — the jobsite lookup won't change this" testId="proposal-tax-chip">Manual {pct(form.tax_rate)}</Chip>
        : <Chip kind="auto" title={taxHint(form.tax_breakdown) || "Auto-looked-up from the jobsite address"} testId="proposal-tax-chip">Auto {pct(form.tax_rate)}</Chip>}
      {customerRule && (
        <Chip kind="manual" testId="proposal-customer-tax-override-badge"
          title={`${form.tax_breakdown?.state} — this customer always bills at ${form.tax_rate}% (${form.tax_authority || "no tax no"}), regardless of the jobsite address`}>CUSTOMER RULE</Chip>
      )}
      {!ov && form.tax_rate_manual && (
        <button type="button" className={railLink} data-testid="proposal-tax-use-auto"
          onClick={() => setForm((f) => ({ ...f, tax_rate_manual: false, tax_rate: f.tax_rate_auto != null ? f.tax_rate_auto : f.tax_rate }))}>
          {form.tax_rate_auto != null ? `use auto (${form.tax_rate_auto}%)` : "use auto"}
        </button>
      )}
      {canOverrideTax && !ov && (
        <button type="button" className={railLink} disabled={!proposalId} onClick={onOverride}
          title={proposalId ? "Manually set the tax rate or amount (requires tax no)" : "Save the proposal first"} data-testid="proposal-tax-override-btn">override</button>
      )}
    </RailRow>
  );
}

// Everything tax- and fee-related that applies to the whole proposal.
export function TaxFeesCard({ form, setForm, totals, proposalId, canOverrideTax, onOverride, applyTaxOverrideResult, onHideTax }) {
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const ccOn = form.cc_fee_pct == null || form.cc_fee_pct > 0;
  return (
    <RailCard title="TAX & FEES" testId="rail-tax-fees">
      <TaxRateRow form={form} setForm={setForm} proposalId={proposalId} canOverrideTax={canOverrideTax} onOverride={onOverride} />
      {form.tax_override && (
        <div className="px-3 py-1.5">
          <TaxOverrideBadge override={form.tax_override} stale={totals.taxOverrideStale} docType="proposal" docId={proposalId}
            canManage={canOverrideTax} onChange={applyTaxOverrideResult} onEdit={onOverride} testId="proposal-tax-override-badge" />
        </div>
      )}
      <RailRow label="Tax No">
        <input value={form.tax_authority || ""} onChange={(e) => set({ tax_authority: e.target.value })} placeholder="Auto"
          className={`${railInputBase} w-24 font-mono`} data-testid="input-proposal-tax-authority" />
      </RailRow>
      <RailRow label="Hide Tax on Customer Copy" hint="No tax line on the customer's proposal — the tax is folded into Material. Still calculated, charged and remitted.">
        <Switch checked={!!form.hide_tax} onCheckedChange={onHideTax} className="h-4 w-7 [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-3" data-testid="checkbox-proposal-hide-tax" />
      </RailRow>
      <RailRow label="CC Fee %" hint="Rolled into Material and the tax base">
        {ccOn ? (
          <>
            <input type="number" step="0.1" min="0" value={form.cc_fee_pct == null ? 0 : form.cc_fee_pct} onChange={(e) => set({ cc_fee_pct: parseFloat(e.target.value) || 0 })}
              onFocus={(e) => e.target.select()} className={railInput} data-testid="input-proposal-cc-fee" />
            <button type="button" onClick={() => set({ cc_fee_pct: 0 })} className="text-slate-300 hover:text-red-500" title="Remove CC fee" data-testid="btn-proposal-remove-cc-fee"><X className="h-3 w-3" /></button>
          </>
        ) : (
          <button type="button" onClick={() => set({ cc_fee_pct: 3.5 })} className={railLink} data-testid="btn-proposal-add-cc-fee">+ Add (3.5%)</button>
        )}
      </RailRow>
      <RailRow label="Discount" hint="Pro-rated across taxable and non-taxable so tax on materials stays honest">
        <div className="flex overflow-hidden rounded-md border border-slate-200 text-[11px]" role="radiogroup">
          {[["flat", "$"], ["pct", "%"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => set({ discount_type: v })} aria-pressed={(form.discount_type || "flat") === v} data-testid={`proposal-discount-type-${v}`}
              className={`h-7 w-7 ${(form.discount_type || "flat") === v ? "bg-slate-200 font-semibold text-slate-900" : "text-slate-500 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <input type="number" step="0.01" min="0" value={form.discount_value == null || form.discount_value === 0 ? "" : form.discount_value} placeholder="0"
          onChange={(e) => set({ discount_value: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} className={railInput} data-testid="input-proposal-discount-value" />
      </RailRow>
      <RailRow label="Breakout on Proposal" hint="Split Material and Labor into separate columns on the PDF and customer preview">
        <Switch checked={!!form.show_breakdown} onCheckedChange={(v) => set({ show_breakdown: v })} className="h-4 w-7 [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-3" data-testid="checkbox-proposal-breakout" />
      </RailRow>
    </RailCard>
  );
}
